import React, { useEffect, useState } from "react";
import {
  ArrowForwardRounded,
  EmailOutlined,
  LockOutlined,
  Visibility,
  VisibilityOff,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import bgImage from "assets/bg.jpeg";
import logoImage from "assets/logo-prima-trans.png";
import { fetchLogin } from "state/userActions";
import { setMessage, setStatus } from "state";

const Login = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { accessToken } = useSelector((state) => state.user);
  const { status, message } = useSelector((state) => state.global);
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  useEffect(() => {
    if (status === "success" && accessToken) navigate("/dashboard");
  }, [status, accessToken, navigate]);

  const handleSubmit = (event) => {
    event.preventDefault();
    dispatch(fetchLogin(email, password));
  };

  const clearAlert = () => {
    dispatch(setStatus("default"));
    dispatch(setMessage(""));
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "grid",
        gridTemplateColumns: {
          xs: "1fr",
          md: "minmax(420px, 1fr) minmax(420px, 0.82fr)",
        },
        bgcolor: "#f5f7fb",
      }}
    >
      <Box
        sx={{
          display: { xs: "none", md: "flex" },
          position: "relative",
          minHeight: "100vh",
          p: 6,
          color: "#fff",
          flexDirection: "column",
          justifyContent: "space-between",
          backgroundImage: `linear-gradient(145deg, rgba(80, 21, 27, 0.97), rgba(137, 20, 24, 0.82)), url(${bgImage})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
          <img
            src={logoImage}
            alt="Prima Centre"
            style={{ width: 100, height: 100, objectFit: "contain" }}
          />
          <Box>
            <Typography sx={{ fontWeight: 800, lineHeight: 1 }}>
              PRIMA CENTRE
            </Typography>
            <Typography
              sx={{
                mt: 0.5,
                color: "#ffc3c4",
                fontSize: 10,
                letterSpacing: 1.8,
                fontWeight: 700,
              }}
            >
              BACK OFFICE
            </Typography>
          </Box>
        </Box>
        <Box sx={{ maxWidth: 470 }}>
          <Typography
            sx={{
              mb: 2,
              color: "#fb8b8b",
              fontSize: 12,
              fontWeight: 800,
              letterSpacing: 1.5,
            }}
          >
            ONE WORKSPACE. BETTER DECISIONS.
          </Typography>
          <Typography
            variant="h2"
            sx={{
              mb: 2,
              fontWeight: 800,
              letterSpacing: "-0.05em",
              lineHeight: 1.08,
            }}
          >
            Semua operasional, satu kendali.
          </Typography>
          <Typography sx={{ color: "#ffe1e1", lineHeight: 1.8, maxWidth: 390 }}>
            Kelola tim, keuangan, absensi, dan aktivitas harian Prima Centre
            melalui ruang kerja yang dirancang untuk bergerak lebih cepat.
          </Typography>
          <Box sx={{ display: "flex", gap: 1, mt: 4 }}>
            {["Data terpusat", "Akses terkontrol", "Insight real-time"].map(
              (item) => (
                <Box
                  key={item}
                  sx={{
                    px: 1.4,
                    py: 0.8,
                    border: "1px solid rgba(255,255,255,.15)",
                    borderRadius: 2,
                    color: "#dce4f2",
                    fontSize: 10.5,
                    fontWeight: 600,
                  }}
                >
                  {item}
                </Box>
              ),
            )}
          </Box>
        </Box>
        <Typography sx={{ color: "#ff9d9e", fontSize: 11 }}>
          © {new Date().getFullYear()} Prima Centre · Internal workspace
        </Typography>
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          px: { xs: 2.5, sm: 5 },
          py: 5,
        }}
      >
        <Paper
          elevation={0}
          sx={{
            width: "100%",
            maxWidth: 450,
            p: { xs: 3, sm: 5 },
            border: "1px solid #e5eaf2",
            borderRadius: 4,
            boxShadow: "0 24px 70px rgba(31, 52, 85, .08)",
          }}
        >
          <Box
            sx={{
              display: { xs: "flex", md: "none" },
              alignItems: "center",
              gap: 1.2,
              mb: 4,
            }}
          >
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: 2,
                display: "grid",
                placeItems: "center",
                bgcolor: "#e5484d",
                overflow: "hidden",
              }}
            >
              <img
                src={logoImage}
                alt="Prima Centre"
                style={{ width: 28, height: 28, objectFit: "cover" }}
              />
            </Box>
            <Typography sx={{ color: "#611920", fontWeight: 800 }}>
              PRIMA CENTRE
            </Typography>
          </Box>
          <Typography
            sx={{
              mb: 1,
              color: "#e5484d",
              fontSize: 11,
              fontWeight: 800,
              letterSpacing: 1.3,
            }}
          >
            WELCOME BACK
          </Typography>
          <Typography
            variant="h3"
            sx={{ color: "#611920", letterSpacing: "-0.05em" }}
          >
            Masuk ke workspace
          </Typography>
          <Typography
            sx={{ mt: 1, color: "#718096", fontSize: 13, lineHeight: 1.6 }}
          >
            Gunakan akun internal Anda untuk melanjutkan ke Prima Centre OS.
          </Typography>

          <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{ mt: 4, display: "grid", gap: 2.25 }}
          >
            <TextField
              label="Email kerja"
              type="email"
              required
              fullWidth
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailOutlined sx={{ color: "#8ea5cb" }} />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              label="Password"
              type={showPassword ? "text" : "password"}
              required
              fullWidth
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockOutlined sx={{ color: "#8ea5cb" }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword((value) => !value)}
                      edge="end"
                      aria-label="Tampilkan password"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mt: -0.5,
              }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    sx={{
                      color: "#ffc3c4",
                      "&.Mui-checked": { color: "#e5484d" },
                    }}
                  />
                }
                label={
                  <Typography sx={{ color: "#718096", fontSize: 11.5 }}>
                    Ingat saya
                  </Typography>
                }
              />
              <Typography
                sx={{ color: "#b82f38", fontSize: 11.5, fontWeight: 700 }}
              >
                Butuh bantuan?
              </Typography>
            </Box>
            <Button
              type="submit"
              variant="contained"
              endIcon={<ArrowForwardRounded />}
              sx={{
                mt: 1,
                py: 1.45,
                bgcolor: "#e5484d",
                boxShadow: "0 10px 22px rgba(229,72,77,.22)",
                "&:hover": { bgcolor: "#c9363d" },
              }}
            >
              Masuk ke dashboard
            </Button>
          </Box>
          <Typography
            sx={{
              mt: 4,
              pt: 2.5,
              borderTop: "1px solid #edf0f5",
              color: "#a0aec0",
              fontSize: 10.5,
              textAlign: "center",
            }}
          >
            Akses ini khusus untuk pengguna internal Prima Centre.
          </Typography>
          {status === "failed" && message && (
            <Alert severity="error" onClose={clearAlert} sx={{ mt: 2 }}>
              {message}
            </Alert>
          )}
        </Paper>
      </Box>
    </Box>
  );
};

export default Login;
